import { useMemo } from 'react';
import { I18nContext } from './I18nProvider';
import useTranslation from './useTranslation';
import { bindMoney } from './money';
import { createFormatters } from './format';

// The formatters that depend on the clock — re-built for a clinic's zone.
const DATE_FORMATTERS = [
  'timeZone', 'formatDate', 'formatTime', 'formatDateTime', 'formatWeekday',
  'formatClinicMonth', 'clinicMonthNames', 'weekdayNames',
];

function dateFormattersFor(locale, timeZone) {
  const all = createFormatters(locale, { timeZone });
  return Object.fromEntries(DATE_FORMATTERS.map((k) => [k, all[k]]));
}

// ─────────────────────────────────────────────────────────────────────────────
// Re-provides the i18n context with its money bound to one currency.
//
// <I18nProvider> is the outermost provider — the sign-in screens need copy long
// before any clinic is known — so the value it provides can only be bound to IRR.
// This provider sits BELOW the clinic context (ClinicContextProvider renders it
// with the active clinic's currency) and hands every screen the same `t`,
// formatters and locale controls, with `formatMoney`, `moneyUnit`, `moneyAmount`,
// the display↔minor converters and the {{unit}}/{{unitShort}} defaults of `t`
// now answering in that currency.
//
//   <CurrencyProvider currency="USD">…</CurrencyProvider>
//
// A blank currency INHERITS the one above — at the top that is IRR (the
// provider under the clinic context always passes a code), and inside a screen
// it lets a document-level provider wrap a document whose query did not select
// `currency` without dropping to IRR. Nesting is fine: each level re-binds from
// the unbound pieces (`moneyBase`), never from an already-bound `t`.
//
// `timeZone` (PM-0088) does the same for the clock: ClinicContextProvider passes the
// active clinic's IANA zone and every formatDate / formatTime below reads it. A
// blank zone inherits the one above.
//
// A DOCUMENT is read in its own currency (CONTRACTS §0.5): wrap what renders one
//   <CurrencyProvider currency={invoice?.currency}>…</CurrencyProvider>
// and everything below formats, labels and parses in it.
// ─────────────────────────────────────────────────────────────────────────────

export function CurrencyProvider({ currency, timeZone, children }) {
  // Outside an <I18nProvider> (a component test) this is the orphan value, which
  // carries the same `moneyBase`; the re-bind still works.
  const parent = useTranslation();
  const code = currency || parent.currency;
  const zone = timeZone || parent.timeZone;

  const value = useMemo(() => {
    if (code === parent.currency && zone === parent.timeZone) return parent;
    return {
      ...parent,
      ...(zone !== parent.timeZone ? dateFormattersFor(parent.locale, zone) : {}),
      ...(code !== parent.currency ? bindMoney(parent.moneyBase, code) : {}),
    };
  }, [parent, code, zone]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * `useTranslation()`, with its money bound to a DOCUMENT's currency — a report
 * root, an invoice — instead of the clinic's:
 *
 *   const { data } = useReport('revenue', params);
 *   const { t, formatMoney, moneyUnit, currency } = useMoneyOf(data?.currency);
 *
 * A blank currency (not loaded yet, or a query that did not select it) is the
 * clinic's, which is what a document of this clinic is in practice.
 */
export function useMoneyOf(currency) {
  const ctx = useTranslation();
  return useMemo(
    () => (!currency || currency === ctx.currency ? ctx : { ...ctx, ...bindMoney(ctx.moneyBase, currency) }),
    [ctx, currency],
  );
}

/**
 * `Component`, rendered inside the currency of the document it is handed:
 *
 *   export const SettleDrawer = inDocumentCurrency(SettleDrawerBody, (p) => p.invoice);
 */
export function inDocumentCurrency(Component, documentOf) {
  function InDocumentCurrency(props) {
    return (
      <CurrencyProvider currency={documentOf(props)?.currency}>
        <Component {...props} />
      </CurrencyProvider>
    );
  }
  InDocumentCurrency.displayName = `InDocumentCurrency(${Component.displayName || Component.name || 'Component'})`;
  return InDocumentCurrency;
}

export default CurrencyProvider;
